import { classNames } from '@dimjs/utils';
import { CSSProperties, FC, Fragment } from 'react';
import {
  CheckList,
  CheckListItem,
  CheckListItemContentProps,
  CheckListItemValue,
  CheckListProps,
} from '../check-list';
import { Grid, GridItem, GridProps } from '../grid';
import { Space } from '../space';
import './style.less';

export type SelectorOptionItem = {
  label: string;
  value: CheckListItemValue;
  labelElement?: JSX.Element;
  disabled?: boolean;
  readOnly?: boolean;
};

export type SelectorItemAdapterProps = {
  label: string | JSX.Element;
  checked?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
};

export type SelectorProps = Omit<CheckListProps, 'checkedClassPrefix'> & {
  gap?: number | [number, number];
  options: Array<SelectorOptionItem>;
  mode: 'freedom' | 'grid';
  gridProps?: {
    columns: number;
    columnHeight?: number;
  };
  freedomProps?: { wrap?: boolean; style?: CSSProperties };
  disabled?: boolean;
  adapter?: (data: SelectorItemAdapterProps) => JSX.Element;
};

type SelectorItemContentProps = CheckListItemContentProps & {
  adapter?: SelectorProps['adapter'];
  label: string | JSX.Element;
};

const SelectorItemContent = (props: SelectorItemContentProps) => {
  return (
    <div className={classNames('v-selector-option', props.className)} onClick={props.onClick}>
      {props.adapter ? (
        <Fragment>
          {props.adapter({
            label: props.label,
            checked: props.checked,
            disabled: props.disabled,
            readOnly: props.readOnly,
          })}
        </Fragment>
      ) : (
        <Fragment>
          {props.label}
          <div className="v-selector-option-icon"></div>
        </Fragment>
      )}
    </div>
  );
};

const CheckListGridChildren = (props: SelectorProps) => {
  const { options, disabled, gap } = props;
  const gridProps = props.gridProps as GridProps;
  return (
    <Grid gap={gap} {...gridProps}>
      {options.map((item) => {
        const label = item.labelElement || item.label;
        return (
          <GridItem key={item.value}>
            <CheckListItem value={item.value} disabled={item.disabled || disabled} readOnly={item.readOnly}>
              <SelectorItemContent label={label} adapter={props.adapter} />
            </CheckListItem>
          </GridItem>
        );
      })}
    </Grid>
  );
};
const CheckListSpaceChildren = (props: SelectorProps) => {
  const { options, disabled, gap, freedomProps, beforeExtra, afterExtra } = props;
  return (
    <Space gap={gap} {...freedomProps}>
      {beforeExtra && <Fragment>{beforeExtra}</Fragment>}
      {options.map((item) => {
        const label = item.labelElement || item.label;
        return (
          <CheckListItem
            value={item.value}
            key={item.value}
            disabled={item.disabled || disabled}
            readOnly={item.readOnly}
          >
            <SelectorItemContent label={label} adapter={props.adapter} />
          </CheckListItem>
        );
      })}
      {afterExtra && <Fragment>{afterExtra}</Fragment>}
    </Space>
  );
};

/**
 * 选择器
 * @param props
 * @returns
 * ```
 * 1. mode：'freedom' | 'grid';
 *    选择器包含两种模式，分别是freedom（自由模式）和grid（网格模式）
 * 2. 自由模式: SelectorItem宽度不固定，随内容而变化; 具体由参数FreedomProps控制
 * 3. 网格模式: SelectorItem宽度为外层宽度的n等分；具体由参数GridProps控制
 * 4. beforeExtra、afterExtra配置在freedom模式下有效
 * ```
 */
export const Selector: FC<SelectorProps> = (props) => {
  const mode = props.mode;

  return (
    <CheckList
      multiple={props.multiple}
      onChange={props.onChange}
      onPreChange={props.onPreChange}
      value={props.value}
      defaultValue={props.defaultValue}
      stopPropagation={props.stopPropagation}
      required={props.required}
      className={classNames('v-selector', `v-selector-${mode}`, props.className)}
      checkedClassPrefix="v-selector"
      style={props.style}
    >
      {props.mode === 'freedom' ? (
        <CheckListSpaceChildren {...props} />
      ) : (
        <CheckListGridChildren {...props} />
      )}
    </CheckList>
  );
};

Selector.defaultProps = {
  gap: 20,
};
